import { useContext, useEffect, useState } from 'react'
import { Navigate, useParams } from 'react-router-dom'
import { KeyRound, Boxes, SlidersHorizontal, Plug, LayoutDashboard } from 'lucide-react'
import { isPlatformUser, AuthContext } from '@retink/web-auth'
import { type NavEntry } from '@retink/web-ui'
import type { Crumb } from '@retink/web-ui'
import { ConsoleShell } from './ConsoleShell'
import { api, switchTenant, signOut, WORKSPACE_URL } from '../lib/api'
import { Button } from '@retink/web-ui'

export function PlatformTenantShell() {
  const { user } = useContext(AuthContext)
  const { tenantId } = useParams<{ tenantId: string }>()
  const [tenantName, setTenantName] = useState<string | null>(null)
  const [org, setOrg] = useState<{ id: string; name: string } | null>(null)

  const isPlatform = isPlatformUser(user)

  useEffect(() => {
    if (!isPlatform || !tenantId) return
    // Note: We use /api/admin/tenants/${tenantId} to get the tenant details
    api.get<{ name: string; org_id: string; org_name: string }>(`/api/admin/tenants/${tenantId}`)
      .then((t) => { setTenantName(t.name); setOrg({ id: t.org_id, name: t.org_name }) })
      .catch(() => { setTenantName(null); setOrg(null) })
  }, [isPlatform, tenantId])

  if (!isPlatform) return <Navigate to="/" replace />
  if (!tenantId) return <Navigate to="/platform/tenants" replace />

  const nav: NavEntry[] = [
    { to: `/platform/t/${tenantId}/signin`, icon: KeyRound, label: 'Sign-in' },
    { to: `/platform/t/${tenantId}/products`, icon: Boxes, label: 'Products' },
    { to: `/platform/t/${tenantId}/features`, icon: SlidersHorizontal, label: 'Features' },
    { to: `/platform/t/${tenantId}/adapters`, icon: Plug, label: 'Adapters' },
  ]

  const breadcrumb: Crumb[] = [
    { label: 'Platform', to: '/platform/orgs' },
    ...(org ? [{ label: org.name, to: `/org/${org.id}/tenants` }] : []),
    { label: tenantName ?? 'Tenant Config' }
  ]

  return (
    <ConsoleShell
      nav={nav}
      onSignOut={signOut}
      navGroup={{ label: 'Tenant', entity: tenantName ?? undefined }}
      breadcrumb={breadcrumb}
      roleLabel={user?.platformRole ?? undefined}
      actions={
        <Button size="sm" variant="outline" onClick={() => switchTenant(tenantId, `${WORKSPACE_URL}/dashboard`)}>
          <LayoutDashboard className="mr-2 size-4" /> Enter Workspace
        </Button>
      }
    />
  )
}
